import { useCallback, useEffect, useRef } from 'react';

import { iaRa } from 'lib/helpers/utility';

const isPersistedResize = ({ source, api }) =>
  source === 'uiColumnResized' ||
  (source === 'autosizeColumns' && !api.getGridOption('autoSizeStrategy'));

export const useColumnPersistence = ({ gridApi, persistColumns, columnsReady }) => {
  const isInitialized = useRef(false);

  useEffect(() => {
    if (!columnsReady) {
      isInitialized.current = false;
      return;
    }

    const timer = setTimeout(() => {
      isInitialized.current = true;
    }, 0);

    return () => clearTimeout(timer);
  }, [columnsReady]);

  const saveColumns = useCallback(
    async (resizedColIds = []) => {
      if (!gridApi || !isInitialized.current) return;
      const resized = new Set(resizedColIds);
      const currentColumns = gridApi
        .getColumnDefs()
        .map((col) => (resized.has(col.colId) ? { ...col, flex: null } : col));
      await persistColumns(currentColumns);
    },
    [gridApi, persistColumns]
  );

  const onColumnMoved = useCallback(() => saveColumns(), [saveColumns]);

  const onColumnResized = useCallback(
    (event) => {
      if (!event.finished || !isPersistedResize(event)) return;
      const flexedColumns = new Set(event.flexColumns);
      const resizedColumns = iaRa(event.columns).filter((col) => !flexedColumns.has(col));
      if (!resizedColumns.length) return;
      event.api.applyColumnState({
        state: resizedColumns.map((col) => ({
          colId: col.getColId(),
          width: col.getActualWidth(),
          flex: null
        }))
      });
      saveColumns(resizedColumns.map((col) => col.getColId()));
    },
    [saveColumns]
  );

  return { onColumnMoved, onColumnResized };
};
